<template>
    <div class="relative">
        <slot :items="paginationData.data"
              :current-page="paginationData.current_page"
              :per-page="resultsPerPage"
              :reorder-offset="reorderOffset"
              :reorder="reorder"
              :show-slide-over="showSlideOver"
              :reload="getData"
        ></slot>

        <nav class="border-t border-gray-200 px-4 flex items-center justify-between sm:px-0 mt-6"
             v-show="paginationData.last_page !== 1"
        >
            <div class="w-0 flex-1 flex">
                <a href="#"
                   class="-mt-px border-t-2 border-transparent pt-4 pr-1 inline-flex items-center text-sm leading-5 font-medium hover:text-gray-700 hover:border-gray-300 focus:outline-none focus:text-gray-700 focus:border-gray-400 transition ease-in-out duration-150"
                   :class="{ 'text-gray-300': paginationData.current_page === 1, 'text-gray-500': paginationData.current_page !== 1 }"
                   @click.prevent="previousPage"
                >
                    <svg class="mr-3 h-5 w-5"
                         :class="{ 'text-gray-300': paginationData.current_page === 1, 'text-gray-400': paginationData.current_page !== 1 }"
                         viewBox="0 0 20 20"
                         fill="currentColor"
                    >
                        <path fill-rule="evenodd" d="M7.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l2.293 2.293a1 1 0 010 1.414z" clip-rule="evenodd" />
                    </svg>
                    Previous
                </a>
            </div>
            <div class="hidden md:flex">
                <a href="#"
                   class="-mt-px border-t-2 pt-4 px-4 inline-flex items-center text-sm leading-5 font-medium focus:outline-none transition ease-in-out duration-150"
                   :class="{ 'border-primary-500 text-primary-600 focus:text-primary-800 focus:border-primary-700': paginationData.current_page === 1, 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 focus:text-gray-700 focus:border-gray-400': paginationData.current_page !== 1 }"
                   @click.prevent="setPage(1)"
                >
                    1
                </a>

                <span class="-mt-px border-t-2 border-transparent pt-4 px-4 inline-flex items-center text-sm leading-5 font-medium text-gray-500"
                      v-show="showLeftEllipsis"
                >
                    ...
                </span>

                <a href="#"
                   class="-mt-px border-t-2 pt-4 px-4 inline-flex items-center text-sm leading-5 font-medium focus:outline-none transition ease-in-out duration-150"
                   :class="{ 'border-primary-500 text-primary-600 focus:text-primary-800 focus:border-primary-700': paginationData.current_page === page, 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 focus:text-gray-700 focus:border-gray-400' : paginationData.current_page !== page}"
                   @click.prevent="setPage(page)"
                   v-for="page in displayPages"
                >
                    {{ page }}
                </a>

                <span class="-mt-px border-t-2 border-transparent pt-4 px-4 inline-flex items-center text-sm leading-5 font-medium text-gray-500"
                      v-show="showRightEllipsis"
                >
                    ...
                </span>

                <a href="#"
                   class="-mt-px border-t-2 pt-4 px-4 inline-flex items-center text-sm leading-5 font-medium focus:outline-none transition ease-in-out duration-150"
                   :class="{ 'border-primary-500 text-primary-600 focus:text-primary-800 focus:border-primary-700': paginationData.current_page === paginationData.last_page, 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 focus:text-gray-700 focus:border-gray-400': paginationData.current_page !== paginationData.last_page }"
                   @click.prevent="setPage(paginationData.last_page)"
                >
                    {{ paginationData.last_page }}
                </a>
            </div>
            <div class="w-0 flex-1 flex justify-end">
                <a href="#"
                   class="-mt-px border-t-2 border-transparent pt-4 pl-1 inline-flex items-center text-sm leading-5 font-medium hover:text-gray-700 hover:border-gray-300 focus:outline-none focus:text-gray-700 focus:border-gray-400 transition ease-in-out duration-150"
                   :class="{ 'text-gray-300': paginationData.current_page === paginationData.last_page, 'text-gray-500': paginationData.current_page !== paginationData.last_page }"
                   @click.prevent="nextPage"
                >
                    Next
                    <svg class="ml-3 h-5 w-5"
                         :class="{ 'text-gray-300': paginationData.current_page === paginationData.last_page, 'text-gray-400': paginationData.current_page !== paginationData.last_page }"
                         viewBox="0 0 20 20"
                         fill="currentColor"
                    >
                        <path fill-rule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clip-rule="evenodd" />
                    </svg>
                </a>
            </div>
        </nav>

        <div class="flex items-center mt-4">
            <span class="text-gray-500 text-sm">Results per page</span>

            <select v-model="resultsPerPage"
                    class="form-select ml-2"
            >
                <option value="25">25</option>
                <option value="30">30</option>
                <option value="50">50</option>
                <option value="60">60</option>
                <option value="75">75</option>
                <option value="90">90</option>
                <option value="100">100</option>
            </select>
        </div>

        <div class="absolute z-10 inset-0 bg-translucent-white-75 flex items-center justify-center"
             v-show="loading"
        >
            <preloader></preloader>
        </div>

        <portal to="slideover">
            <slide-over :heading="slideOverHeading"
                        ref="slideOver"
                        v-if="displaySlideOver"
                        @hide="displaySlideOver = false"
            >
                <slot name="slideOverBody"></slot>

                <template slot="footer">
                    <slot name="slideOverFooter"
                          :hide-slide-over="hideSlideOver"
                          :get-data="getData"
                    ></slot>
                </template>
            </slide-over>
        </portal>
    </div>
</template>

<script>
    import Preloader from "./Preloader";
    import SlideOver from "./SlideOver";

	export default {
	    data() {
	        return {
	            paginationData: {},

                loading: false,
                displaySlideOver: false,

                resultsPerPage: null,
            }
        },

		props: {
		    dataUrl: {
		        type: String,
                required: true,
            },

            filters: {
		        type: Object,
                default: () => {
                    return {};
                }
            },

            perPage: {
		        type: Number,
                default: 25,
            },

            slideOverHeading: {
		        type: String,
            }
        },

        components: { Preloader, SlideOver },

        watch: {
	        'paginationData.current_page': function () {
	            this.getData();
            },

            resultsPerPage: function () {
	            this.getData();
            },
        },

        created() {
	        this.resultsPerPage = this.perPage;
        },

        computed: {
            displayPages() {
                if (this.paginationData.last_page <= 7) {
                    return _.range(2, this.paginationData.last_page);
                }

                if (this.paginationData.current_page <= 4) {
                    return _.range(2, _.min([6, this.paginationData.last_page]));
                }

                if (this.paginationData.current_page >= 4 && this.paginationData.last_page - this.paginationData.current_page >= 3) {
                    return [this.paginationData.current_page - 1, this.paginationData.current_page, this.paginationData.current_page + 1];
                }

                if (this.paginationData.last_page - this.paginationData.current_page < 3) {
                    return _.range(this.paginationData.last_page - 3, this.paginationData.last_page)
                }
            },

            showLeftEllipsis() {
                if (this.paginationData.last_page <= 7) return false;

                return this.paginationData.current_page > 4;
            },

            showRightEllipsis() {
                if (this.paginationData.last_page <= 7) return false;

                return this.paginationData.last_page - this.paginationData.current_page >= 3;
            },

            reorderOffset() {
                return (this.paginationData.current_page - 1) * this.resultsPerPage
            }
        },

        methods: {
            getData() {
                this.loading = true;

                this.hideSlideOver();

                axios.get(this.dataUrl, {
                    params: {
                        page: this.paginationData.current_page,
                        filters: this.filters,
                        per_page: this.resultsPerPage
                    }
                }).then(response => {
                    this.paginationData = response.data;
                    this.loading = false;
                })
                .catch(() => {
                    this.loading = false;
                })
            },

            nextPage() {
                if (this.paginationData.current_page === this.paginationData.last_page) return false;

                this.paginationData.current_page++;
            },

            previousPage() {
                if (this.paginationData.current_page === 1) return false;

                this.paginationData.current_page--;
            },

            setPage(page) {
                this.paginationData.current_page = page;
            },

            reorder(data) {
                this.paginationData.data = data;
            },

            showSlideOver() {
                this.displaySlideOver = true;
            },

            hideSlideOver() {
                if (this.displaySlideOver) {
                    this.$refs.slideOver.hide();
                }
            }
        }
    }
</script>
